import { AfterViewInit, Component, DoCheck, ElementRef, EventEmitter, forwardRef, HostBinding, inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatFormFieldControl, MatSuffix }                                                                                                    from '@angular/material/form-field';
import { ControlValueAccessor, FormGroupDirective, FormsModule, NgControl, NgForm, ReactiveFormsModule }                                     from '@angular/forms';
import { coerceBooleanProperty }                                                                                                             from '@angular/cdk/coercion';
import { MatInput }                                                                                                                          from '@angular/material/input';
import { CdkAutofill }                                                                                                                       from '@angular/cdk/text-field';
import { MatIcon }                                                                                                                           from '@angular/material/icon';
import { Subject }                                                                                                                           from 'rxjs';

class PasswordInputBase {
  constructor(public _parentForm: NgForm,
              public _parentFormGroup: FormGroupDirective,
              public ngControl: NgControl,
              public stateChanges: Subject<void>) {
  }
}

@Component({
  selector: 'pin-password-input',
  templateUrl: './password-input.component.html',
  providers: [
    {provide: MatFormFieldControl, useExisting: forwardRef(() => PasswordInputComponent), multi: true}
  ],
  imports: [MatInput, ReactiveFormsModule, FormsModule, CdkAutofill, MatIcon, MatSuffix]
})
export class PasswordInputComponent
  extends PasswordInputBase
  implements AfterViewInit, OnDestroy, ControlValueAccessor, DoCheck, MatFormFieldControl<string> {
  elementRef = inject(ElementRef);

  hidePassword = true;
  focused = false;
  autofilled = false;
  stateChanges = new Subject<void>();

  @ViewChild('inputElement')
  inputElement;
  @HostBinding('attr.aria-describedby')
  describedBy = '';
  @Input()
  allowPaste = true;
  @Output()
  passwordFocus = new EventEmitter<FocusEvent>();
  @Output()
  passwordBlur = new EventEmitter<FocusEvent>();

  constructor() {
    const ngControl = inject(NgControl, {optional: true, self: true});
    const _parentForm = inject(NgForm, {optional: true});
    const _parentFormGroup = inject(FormGroupDirective, {optional: true});
    const _stateChanges = inject<Subject<void>>(Subject, {optional: true});

    super(_parentForm, _parentFormGroup, ngControl, _stateChanges);
    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
  }

  private _placeholder: string;

  @Input()
  get placeholder() {
    return this._placeholder;
  }

  set placeholder(newPlaceholder) {
    this._placeholder = newPlaceholder;
  }

  private _value = '';

  @Input()
  get value() {
    return this._value;
  }

  set value(newValue) {
    this._value = newValue;
  }

  private _required = undefined;

  @Input()
  get required() {
    return this._required;
  }

  set required(req) {
    this._required = coerceBooleanProperty(req);
  }

  private _disabled = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }

  private _id: string;

  @Input()
  get id(): string {
    return this._id;
  }

  set id(value: string) {
    this._id = value;
  }

  get errorState() {
    return this.ngControl.errors !== null && this.ngControl.invalid && ((this.ngControl.touched && !this.focused) || this.ngControl.dirty);
  }

  get empty() {
    return (this.value == null || this.value.length === 0) && !this.autofilled;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  ngAfterViewInit(): void {
    this.setLabelForChildInputElement();
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.passwordFocus.complete();
    this.passwordBlur.complete();
  }

  changeState() {
    this.stateChanges.next();
    this.onTouched(this.value);
    this.onChange(this.value);
  }

  onPasswordFieldChange() {
    this.changeState();
  }

  onPasswordFieldFocus(event: FocusEvent) {
    this.focused = true;
    this.passwordFocus.emit(event);
  }

  onPasswordFieldBlur(event: FocusEvent) {
    this.focused = false;
    this.changeState();
    this.passwordBlur.emit(event);
  }

  onPasswordFieldPaste(event: ClipboardEvent) {
    if (!this.allowPaste) {
      event.preventDefault();
    }
  }

  togglePasswordReveal(event?: KeyboardEvent | null) {
    if (event === undefined || event.key === ' ' || event.key === 'Enter' || event.key === 'Spacebar') {
      this.hidePassword = !this.hidePassword;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  // MatFormFieldControl functions / fields
  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  onContainerClick = (_event: MouseEvent) => { /* by default do nothing */
  };

  // ControlValueAccessor functions
  writeValue(obj: string): void {
    this.value = obj;
  }

  onChange = (_) => { /* by default do nothing */
  };

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  onTouched = (_) => { /* by default do nothing */
  };

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  ngDoCheck() {
    if (this.ngControl) {
      const _check = this.errorState;
    }
  }

  private setLabelForChildInputElement() {
    let currentElement: HTMLElement = this.elementRef.nativeElement;
    while (currentElement && currentElement.tagName.toLowerCase() !== 'mat-form-field') {
      currentElement = currentElement.parentElement;
    }
    if (currentElement != null) {
      currentElement.querySelector('label').setAttribute('for', this.inputElement.nativeElement.id);
    }
  }
}
